import { router, useLocalSearchParams } from 'expo-router';
import { useCallback, useMemo, useState } from 'react';

import { useCreateOtpv2Mutation } from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import { useAmplitudeContext } from '@fhs-legacy/frontend/src/state/amplitude';

import { useAuthRouteBase } from '../auth-route-base-context';
import { AuthIdentifiers } from '../types';

import { useAuthNavigationParams } from './use-auth-navigation-params';

import { useCRMEventsContext, useToast } from '.';

export type CreateOtpInput = {
  loginIdentifier: string;
  identifierType: AuthIdentifiers;
};

export const useCreateOtp = () => {
  const [otpLoading, setOtpLoading] = useState<boolean>(false);
  const [otpError, setOtpError] = useState<boolean>(false);
  const { show } = useToast();
  const { logCreateOtpAttempt } = useCRMEventsContext();
  const { loginJwt } = useLocalSearchParams<{ loginJwt?: string }>();
  const { paths } = useAuthRouteBase();
  const authNavigationParams = useAuthNavigationParams();
  const { deviceId } = useAmplitudeContext();
  /**
   * Get consistent login attempt parameters
   * Stub implementation for device ID for now
   */
  const persistentParameters = useMemo(
    () => ({
      deviceId,
      loginJwt,
    }),
    [loginJwt, deviceId]
  );
  const onCreateOtp = useCallback(
    data => {
      const params = {
        ...authNavigationParams,
        loginJwt: data.createOtpV2.loginJwt,
      };
      if (loginJwt) {
        router.replace({
          pathname: paths.verify,
          params,
        });
      } else {
        router.push({
          pathname: paths.verify,
          params,
        });
      }
      return data;
    },
    [authNavigationParams, loginJwt, paths.verify]
  );

  const onError = useCallback(
    err => {
      if (err?.graphQLErrors && err.graphQLErrors.length > 0) {
        //Take the highest level error code
        const code = err.graphQLErrors[0].extensions.code;
        switch (code) {
          case 'TOO_MANY_REQUESTS':
            show({
              text: 'You have exceeded the limit of attempts. Please try again later.',
              variant: 'informative',
            });
            return;
        }
      }
      show({ text: 'Oops, something went wrong. Please try again', variant: 'negative' });
      setOtpError(true);
    },
    [show]
  );
  const [internalCreateOtp] = useCreateOtpv2Mutation({
    onCompleted: onCreateOtp,
    onError,
  });
  const createOtp = useCallback(
    async (partialInput: CreateOtpInput) => {
      setOtpLoading(true);
      setOtpError(false);
      //Push event to Amplitude
      logCreateOtpAttempt({ identifierType: partialInput.identifierType });
      //Process Create OTP request
      await internalCreateOtp({
        variables: {
          input: {
            ...persistentParameters,
            ...partialInput,
          },
        },
      });
      setOtpLoading(false);
    },
    [internalCreateOtp, persistentParameters, logCreateOtpAttempt]
  );
  return {
    createOtp,
    otpError,
    otpLoading,
  };
};
